/**
 * /compliance/popia — POPIA compliance posture.
 *
 * Public reference page covering Elreign's role as Operator, data residency,
 * DSAR flow, data-subject rights, breach notification, and the interaction
 * between POPIA s14 retention and FICA s23 record-keeping.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/compliance/popia')({
  component: CompliancePopiaPage,
});

const TOC = [
  { anchor: 'role',         label: 'Our role as Operator' },
  { anchor: 'io',           label: 'Information Officer designation' },
  { anchor: 'residency',    label: 'Data residency' },
  { anchor: 'rights',       label: 'Data subject rights' },
  { anchor: 'dsar',         label: 'DSAR fulfilment' },
  { anchor: 'erasure',      label: 'Erasure and FICA s23' },
  { anchor: 'breach',       label: 'Breach notification' },
  { anchor: 'transfers',    label: 'Cross-border transfers' },
];

function CompliancePopiaPage() {
  useEffect(() => {
    document.title = 'POPIA compliance · Elreign: operator posture, residency, DSAR flow';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'POPIA compliance posture: operator role under POPIA s1, Information Officer designation, southafricanorth residency, DSAR fulfilment, erasure under FICA s23, breach notification, and cross-border safeguards.',
    );
  }, []);

  return (
    <LegalLayout heading="POPIA compliance." lastUpdated="17 May 2026" toc={TOC}>
      <p>
        Elreign processes personal information as Operator under the Protection of Personal
        Information Act (Act 4 of 2013) on behalf of tenant firms acting as Responsible Party.
        This page describes how we implement our obligations under POPIA and how we support the
        tenant firm in implementing its own.
      </p>

      <h2 id="role">Our role as Operator</h2>
      <p>
        Under POPIA s1, an Operator is a person who processes personal information on behalf of
        a Responsible Party. We process personal information only on the documented instructions
        of the tenant firm. Where the firm&apos;s instructions, in our reasonable opinion,
        infringe POPIA or the General Data Protection Regulation (where applicable), we notify
        the firm in writing and may suspend the affected processing until the firm confirms or
        amends the instruction. The full role allocation is recorded in our Data Processing
        Agreement at <Link to="/legal/dpa">/legal/dpa</Link>.
      </p>

      <h2 id="io">Information Officer designation</h2>
      <p>
        POPIA s55 requires the appointment of an Information Officer for each responsible party
        and operator. Elreign has designated its Information Officer; registration of the
        designation with the Information Regulator is in progress. The platform supports tenant
        firms in designating their own Information Officer and any Deputy Information Officers
        in product and preparing the registration submission, along with the Personal
        Information Impact Assessment (PIA) and PAIA manual.
      </p>

      <h2 id="residency">Data residency</h2>
      <p>
        Substantive personal information (trust records, beneficial owner data, FICA documents,
        signing artefacts, audit chain) is stored in <strong>Azure South Africa North</strong>
        (Johannesburg primary) with geo-redundant fallback to <strong>Azure South Africa West</strong>
        (Cape Town). Authentication metadata only (email address, session tokens, MFA enrolment
        state) is stored in Supabase EU West (Ireland) under the safeguards described below.
        No substantive personal information leaves South Africa.
      </p>

      <h2 id="rights">Data subject rights</h2>
      <p>
        POPIA grants data subjects a set of rights including right of access (s23), right to
        correction or deletion (s24), and right to object to processing (s11(3)). Through the
        tenant firm, every data subject (beneficiaries, trustees, founders) may
        exercise these rights with respect to personal information held on the platform.
      </p>

      <h2 id="dsar">DSAR fulfilment</h2>
      <p>
        Data subject access requests (DSARs) reaching the tenant firm are managed through the
        in-product DSAR queue with a 30-day statutory clock, structured fulfilment workflow, and
        a templated response that includes every document, message, signature event, and audit
        entry concerning the data subject. The fulfilment record is sealed on the audit chain.
      </p>
      <p>
        DSARs reaching Elreign directly are routed to the tenant firm within 48 hours, in
        keeping with the operator boundary; the data subject is not responded to directly.
      </p>

      <h2 id="erasure">Erasure and FICA s23</h2>
      <p>
        Erasure under POPIA s24 must respect statutory retention windows that apply elsewhere,
        notably FICA s23, which requires beneficial ownership records to be kept for at least
        five years after the end of a business relationship. The platform reconciles these
        regimes by separating record retention from personal information retention.
      </p>
      <p>
        When erasure is requested and granted, the personal information layer can be erased, on
        request, through an approval-gated, dual-control cryptographic key-destruction procedure,
        with each step sealed on the audit chain. The audit chain itself remains intact for the
        FICA retention window; when the protecting keys are destroyed, the personal information
        embedded in it is rendered unrecoverable. After the FICA retention window expires, the
        chain entries themselves are pruned.
      </p>

      <h2 id="breach">Breach notification</h2>
      <p>
        On confirming a personal information breach, we notify the tenant firm&apos;s designated
        security contact within 72 hours of confirmation, in keeping with POPIA s22 and our DPA.
        The notification covers the nature of the breach, affected categories of personal
        information, likely consequences, and the measures taken or proposed. Tenant firms then
        notify their data subjects and the Information Regulator as required by s22; we provide
        supporting evidence on request.
      </p>

      <h2 id="transfers">Cross-border transfers</h2>
      <p>
        The only cross-border transfer in our processing is authentication metadata to Supabase
        EU West. This is permitted under POPIA s72 because Ireland (an EU member state) is
        recognised as providing an adequate level of protection equivalent to South African
        POPIA requirements by virtue of its compliance with the General Data Protection
        Regulation. The transfer is further governed by standard contractual clauses adopted
        under GDPR Article 46(2)(c).
      </p>

      <p style={{ marginTop: 32 }}>
        See also: <Link to="/legal/privacy">Privacy policy</Link>,{' '}
        <Link to="/legal/dpa">Data Processing Agreement</Link>,{' '}
        <Link to="/legal/subprocessors">Sub-processor list</Link>,{' '}
        <Link to="/compliance/fica">FICA compliance posture</Link>.
      </p>
    </LegalLayout>
  );
}
